import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { RiFullscreenExitLine, RiFullscreenLine } from "@remixicon/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";
import { chain } from "lodash";
import ReviewNonOmniBizCategories from "./ReviewNonOmniBizCategories";
import ReviewNonOmniNames from "./ReviewNonOmniNames";
import ReviewNonOmniWebsites from "./ReviewNonOmniWebsites";
import ReviewNonOmniCountryCount from "./ReviewNonOmniCountryCount";
import ReviewNonOmniFillRates from "./ReviewNonOmniFillRates";
import ReviewPlacesParentLocation from "./ReviewPlacesParentLocation";
import ReviewPlacesIncorrectParent from "./ReviewPlacesIncorrectParent";

interface PlacesReviewNonOmniProps {
  chain_id: string;
  brand_id: string;
  brand_name: string;
}

const PlacesReviewLocationParent: React.FC<PlacesReviewNonOmniProps> = ({
  chain_id,
  brand_id,
  brand_name,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [nonOmniCountries, setNonOmniCountries] = useState<any[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );

  const { showError } = useError();
  const { apiRequest } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  useEffect(() => {
    const fetchNonOmniCountries = async () => {
      try {
        const response = await apiRequest<
          { country_code: string | null | undefined }[]
        >(`/brand_utils/non_omni_country_list/${chain_id}`, {
          method: "GET",
        });

        if (response.data) {
          const validCountries = response.data.filter(
            (item): item is { country_code: string } =>
              typeof item.country_code === "string" &&
              item.country_code.trim() !== ""
          );

          const sortedCountries = [...validCountries].sort((a, b) =>
            a.country_code.localeCompare(b.country_code)
          );
          setNonOmniCountries(sortedCountries);

          const params = new URLSearchParams(location.search);
          const countryCode = params.get("country");
          if (
            countryCode &&
            sortedCountries.some((c) => c.country_code === countryCode)
          ) {
            setSelectedCountryCode(countryCode);
          }
        }
      } catch (error) {
        showError(
          `Failed to fetch countries for brand non-omni review dropdown. Chain ID: ${chain_id}`
        );
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchNonOmniCountries();
  }, [location.search]);

  // Update URL when the selectedCountryCode changes
  useEffect(() => {
    if (selectedCountryCode) {
      const params = new URLSearchParams(location.search);
      params.set("country", selectedCountryCode);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [selectedCountryCode, location.search, navigate]);

  // User Instructions for Location Parent Review
  const user_instructions = {
    section_id: "places-location-parent",
    addAnchorLink: false,
    instruction_title: "Location Parent Review",
    task_description:
      "The location_parent_id is used to establish parent-child relationships between physical locations, particularly for places that are contained within or colocated with other places.",
    task_steps: [
      {
        title: "Review the following key aspects of location_parent_id",
        instructions: [
          {
            text: "Key aspects:",
            sublist: [
              "It's used to identify when one place is physically located inside or associated with another place, like departments within a big-box store (e.g., a Target Optical inside a Target store)",
              'For colocated businesses (like a gas station next to a store), the location_parent_id can point to the main location, even if they\'re physically separated but associated. The definition of "colocated" is flexible - it could be right next to the store, across the parking lot, or even across the street.',
              'This relationship helps maintain proper hierarchies, especially for "big-box" brands that have multiple sub-POIs (Points of Interest) within them.',
            ],
          },
        ],
      },
    ],
    note: "A place should not have its own ID as its location_parent_id - this is considered invalid and needs to be corrected when found.",
  };

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              overflow: "auto",
              p: 5,
              pt: 0,
            }
          : {
              position: "relative",
              width: "auto",
              height: "auto",
              minHeight: "500px",
              overflow: "hidden",
              border: "1px solid #ccc",
              borderRadius: "4px",
              p: 5,
              mt: 2,
              backgroundColor: "#fff",
            }
      }
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          ...(isFullscreen && {
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            width: "100%",
            zIndex: 1000,
            p: 2,
          }),
        }}
      >
        <Typography variant="h5">
          {isFullscreen
            ? `Brand Non-Omni Review: ${brand_name} - ${
                selectedCountryCode?.toUpperCase() ||
                "Select an country to review"
              }`
            : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Open Kuebiko Button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              <img
                src="/images/dataplor_logo.svg"
                alt="dataplor logo"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={() =>
              window.open(
                `https://kuebiko.dataplor.com/brands/${brand_id}/review`,
                "_blank"
              )
            }
          >
            Open Kuebiko
          </Button>
          {/* Fullscreen Toggle Button */}
          <IconButton onClick={toggleFullscreen} sx={{ zIndex: 500 }}>
            {isFullscreen ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: 2, pl: 2, pr: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Location Parent Review
          </Typography>
        </Box>

        <Box sx={{ mt: 5 }}>
          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" gutterBottom>
              Review Location Parent Data by Country
            </Typography>
            <Typography paragraph sx={{ fontStyle: "italic" }}>
              Iterate through one country at a time. Select a country below to
              update the data for review.
            </Typography>
          </Box>
          {/* Country Code Dropdown */}
          <Box sx={{ mt: 2, mb: 5, width: 300 }}>
            {loadingCountries ? (
              <CircularProgress />
            ) : (
              <Autocomplete
                options={nonOmniCountries}
                getOptionLabel={(option) =>
                  `${option.country_code.toUpperCase()}: ${option.country_name}`
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter(
                    (option) =>
                      option.country_code
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()) ||
                      option.country_name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Country"
                    variant="outlined"
                  />
                )}
                value={
                  nonOmniCountries.length > 0
                    ? nonOmniCountries.find(
                        (country) =>
                          country.country_code === selectedCountryCode
                      ) || null
                    : null
                }
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedCountryCode(newValue.country_code);
                  } else {
                    setSelectedCountryCode(null);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option.country_code === value.country_code
                }
              />
            )}
          </Box>
          {/* Country Specific Content */}
          {selectedCountryCode && (
            <Box>
              <BrandReviewInstructions {...user_instructions} />
              <ReviewPlacesParentLocation
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
              <ReviewPlacesIncorrectParent
                chain_id={chain_id}
                country_code={selectedCountryCode}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PlacesReviewLocationParent;
